import React, { useState, useEffect, useRef } from "react";
import { supabase } from "../supabase/client.js";
import { useLocation } from "react-router-dom";
import { useAdminAuth } from "../auth/useAdminAuth.js";

// Icons
const UserIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
);
const LogoutIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path><polyline points="16 17 21 12 16 7"></polyline><line x1="21" y1="12" x2="9" y2="12"></line></svg>
);

export default function Navbar({ toggleSidebar }) {
  const location = useLocation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [admin, setAdmin] = useState(null);
  const dropdownRef = useRef(null);
  const { logout } = useAdminAuth();

  // 1. Fetch from updated 'admins' table
  // Inside your Navbar Component
useEffect(() => {
  async function fetchAdmin() {
    const loggedInUser = localStorage.getItem("admin_user");
    if (!loggedInUser) return;

    const { data } = await supabase
      .from('admins')
      .select('username, role')
      .eq('username', loggedInUser) 
      .single();

    if (data) setAdmin(data);
  }
  fetchAdmin();
}, []);

  // Title Logic
  const getPageTitle = (pathname) => {
    const path = pathname.replace(/^\//, '');
    if (path === '') return 'Dashboard';
    if (path === 'ads') return 'Ads & Banners';
    return path.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  // Close dropdown on click outside
  useEffect(() => {
    const clickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsDropdownOpen(false);
    };
    document.addEventListener("mousedown", clickOutside);
    return () => document.removeEventListener("mousedown", clickOutside);
  }, []);

  const displayName = admin?.username || "Admin";
  const displayRole = admin?.role || "Editor";
  const initial = displayName[0].toUpperCase();

  return (
    <header className="h-[80px] md:h-[80px] h-[60px] bg-white/95 backdrop-blur-[10px] flex items-center justify-between px-[15px] md:px-[30px] sticky top-0 z-50 border-b border-black/5 shadow-[0_4px_20px_rgba(0,0,0,0.02)]">
      
      {/* LEFT SECTION */}
      <div className="flex items-center gap-[10px] md:gap-[20px]">
        <button 
          onClick={toggleSidebar}
          className="text-2xl p-1.5 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer text-[#333]"
        >
          ☰
        </button>
        <div className="flex flex-col">
          <h1 className="text-[1.1rem] md:text-[1.5rem] font-bold text-[#1a1a1a] leading-[1.2]">
            {getPageTitle(location.pathname)}
          </h1>
          <p className="hidden md:block text-[0.85rem] text-[#888] font-medium">
            Curve & Fit Admin
          </p>
        </div>
      </div>

      {/* RIGHT SECTION */}
      <div className="relative" ref={dropdownRef}>
        <div 
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          className={`cursor-pointer p-1 rounded-full border-2 transition-all duration-200 
            ${isDropdownOpen ? 'border-[#f525bd] bg-[#fff0f9]' : 'border-transparent hover:border-[#f525bd] hover:bg-[#fff0f9]'}`}
        >
          <div className="w-[35px] h-[35px] md:w-[42px] md:h-[42px] bg-gradient-to-br from-[#111] to-[#333] text-white rounded-full flex items-center justify-center font-bold text-[14px] md:text-[18px] shadow-[0_4px_10px_rgba(0,0,0,0.15)]">
            {initial}
          </div>
        </div>

        {/* PREMIUM DROPDOWN */}
        <div className={`absolute top-[60px] right-0 w-[260px] bg-white rounded-[16px] shadow-[0_10px_40px_rgba(0,0,0,0.12),0_2px_10px_rgba(0,0,0,0.05)] border border-black/5 p-2 transition-all duration-200 cubic-bezier(0.16,1,0.3,1)
          ${isDropdownOpen ? 'opacity-100 translate-y-0 scale-100 visible' : 'opacity-0 -translate-y-[10px] scale-95 invisible'}`}
        >
          <div className="p-4 flex items-center gap-3">
            <div className="w-9 h-9 bg-gray-100 rounded-full flex items-center justify-center text-gray-500">
              <UserIcon />
            </div>
            <div className="flex flex-col overflow-hidden">
              <span className="font-semibold text-[0.95rem] text-[#111] truncate">{displayName}</span>
              <span className="text-[0.75rem] text-pink-600 font-bold uppercase tracking-tight">
                {displayRole}
              </span>
            </div>
          </div>

          <div className="h-[1px] bg-gray-100 my-1" />

          <button 
      onClick={logout} // Use the hook's logout function here
      className="w-full p-3 flex items-center text-[0.9rem] font-medium text-red-600 rounded-lg hover:bg-red-50 transition-all"
    >
      <LogoutIcon /> Sign Out
    </button>
        </div>
      </div>
    </header>
  );
}